import {
	UntypedFormGroup,
	UntypedFormControl,
	Validators,
	UntypedFormArray,
} from '@angular/forms';
import { Validation } from './validation.model';
import { BaseResponse } from './base-response.model';
import {
	CustomReportDisplayFieldJSON,
	CustomReportDisplayField,
} from './custom-report-display-field.model';
import { CustomReportTable } from './custom-report-table.model';
import {
	CustomReportCriteriaJSON,
	CustomReportCriteria,
} from './custom-report-criteria.model';
import { CustomReportOperator } from './custom-report-operator.model';
import { DateTimeUtility } from '../utility/date-time.utility';

export interface CustomReportsResponse extends BaseResponse {
	message: string;
	reports: CustomReportJSON[];
}

export interface CustomReportResponse extends BaseResponse {
	message: string;
	report: CustomReportJSON;
	fields: CustomReportDisplayFieldJSON[];
	criteria: CustomReportCriteriaJSON[];
}

export interface CustomReportJSON {
	id: number;
	myDBRReportId: number;
	reportName: string;
	activeUsers: boolean;
	allRecords: boolean;
	updatedBy: string;
	lastUpdated: string;
}

export interface SaveCustomReportData {
	id: number;
	reportName: string;
	activeUsers: boolean;
	allRecords: boolean;
	criteria: CustomReportCriteria[];
	fields: CustomReportDisplayField[];
}

export class CustomReport {
	constructor(
		public id: number,
		public myDBRReportId: number,
		public reportName: string,
		public activeUsers: boolean,
		public allRecords: boolean,
		public updatedBy: string,
		public lastUpdated: Date
	) {}

	static fromJSON(json: CustomReportJSON): CustomReport {
		return Object.assign(Object.create(CustomReport.prototype), json, {
			lastUpdated: json.lastUpdated
				? DateTimeUtility.fromDatabaseDatetime(json.lastUpdated)
				: null,
		});
	}

	static createFormGroup(
		r: CustomReport,
		fields: CustomReportDisplayField[],
		criteria: CustomReportCriteria[],
		tables: CustomReportTable[],
		dateOperators: CustomReportOperator[],
		stringOperators: CustomReportOperator[],
		validation: Validation
	): UntypedFormGroup {
		const fg: UntypedFormGroup = new UntypedFormGroup({
			id: new UntypedFormControl(r ? r.id : null),
			myDBRReportId: new UntypedFormControl(r ? r.myDBRReportId : null),
			reportName: new UntypedFormControl(r ? r.reportName : null, [
				Validators.required,
				Validators.maxLength(validation.storedProcName.maxLength),
				Validators.pattern(validation.storedProcName.pattern),
			]),
			activeUsers: new UntypedFormControl(r ? r.activeUsers : true),
			filterByCriteria: new UntypedFormControl(r ? !r.allRecords : false),
			fields: CustomReportDisplayField.createFormArray(
				fields,
				tables,
				validation
			),
			criteria: CustomReportCriteria.createFormArray(
				criteria,
				tables,
				dateOperators,
				stringOperators,
				validation
			),
		});
		if (r !== null) {
			fg.get('reportName').disable();
		}
		return fg;
	}

	static fromFormGroup(fg: UntypedFormGroup): SaveCustomReportData {
		return {
			id: fg.get('id').value,
			reportName: fg.get('reportName').value,
			activeUsers: fg.get('activeUsers').value,
			allRecords: !fg.get('filterByCriteria').value,
			criteria: CustomReportCriteria.fromFormArray(
				fg.get('criteria') as UntypedFormArray
			),
			fields: CustomReportDisplayField.fromFormArray(
				fg.get('fields') as UntypedFormArray
			),
		};
	}
}
